import { useState } from 'react';
import { useInitiativeTemplates } from '@/hooks/use-initiative-templates';
import { t } from '@/lib/i18n/messages';
import type { Locale } from '@/types/auth';
import type { InitiativeTemplate, TemplateCategory } from '@/types/initiative';

interface WizardStepTemplateProps {
  locale: Locale;
  selectedTemplateId: string | null;
  onSelect: (template: InitiativeTemplate) => void;
}

type CategoryFilter = 'all' | TemplateCategory;

const categoryFilters: CategoryFilter[] = ['all', 'social', 'community', 'environmental'];

const categoryIcons: Record<TemplateCategory, string> = {
  social: 'group',
  community: 'home',
  environmental: 'eco',
};

// Category accents mirror the brand convention: social = orange, community = blue, environmental = green.
const categoryAccents: Record<TemplateCategory, { active: string }> = {
  social: { active: 'bg-secondary/10 dark:bg-secondary/20 text-secondary border border-secondary/30' },
  community: { active: 'bg-brand-blue/10 dark:bg-brand-blue/20 text-brand-blue border border-brand-blue/30' },
  environmental: { active: 'bg-brand-green/10 dark:bg-brand-green/20 text-brand-green border border-brand-green/30' },
};

// Template card accents keyed by slug: Walk = blue, Juthoor = green.
const templateAccents: Record<string, { ring: string; border: string; bg: string; icon: string; hover: string }> = {
  walk_initiative: {
    ring: 'focus:ring-brand-blue/50',
    border: 'border-brand-blue',
    bg: 'bg-brand-blue/5 dark:bg-brand-blue/10',
    icon: 'text-brand-blue',
    hover: 'hover:border-brand-blue',
  },
  juthoor_initiative: {
    ring: 'focus:ring-brand-green/50',
    border: 'border-brand-green',
    bg: 'bg-brand-green/5 dark:bg-brand-green/10',
    icon: 'text-brand-green',
    hover: 'hover:border-brand-green',
  },
};

const defaultTemplateAccent = {
  ring: 'focus:ring-secondary/50',
  border: 'border-secondary',
  bg: 'bg-secondary/5 dark:bg-secondary/10',
  icon: 'text-secondary',
  hover: 'hover:border-secondary',
};

function getTemplateAccent(slug: string) {
  return templateAccents[slug] ?? defaultTemplateAccent;
}

export function WizardStepTemplate({
  locale,
  selectedTemplateId,
  onSelect,
}: WizardStepTemplateProps) {
  const [activeCategory, setActiveCategory] = useState<CategoryFilter>('all');
  const { templates, loading } = useInitiativeTemplates();
  const isRTL = locale === 'ar';

  const filteredTemplates =
    activeCategory === 'all'
      ? templates
      : templates.filter((tpl) => tpl.category === activeCategory);

  const getCategoryLabel = (cat: CategoryFilter) => {
    if (cat === 'all') return t(locale, 'all');
    if (cat === 'social') return t(locale, 'templateSocial');
    if (cat === 'community') return t(locale, 'templateCommunity');
    return t(locale, 'templateEnvironmental');
  };

  const getTemplateName = (template: InitiativeTemplate) =>
    locale === 'ar' ? template.name_ar : template.name_en;

  const getTemplateDescription = (template: InitiativeTemplate) =>
    locale === 'ar' ? template.description_ar : template.description_en;

  return (
    <div className="space-y-6">
      {/* Category Filter Pills */}
      <div>
        <p className={`text-sm font-semibold text-slate-700 mb-4 ${isRTL ? 'text-right' : 'text-left'}`}>
          {t(locale, 'selectTemplate')} <span className="text-red-500">*</span>
        </p>
        <div className="flex flex-wrap gap-3">
          {categoryFilters.map((cat) => {
            const isActive = activeCategory === cat;
            return (
              <button
                key={cat}
                type="button"
                onClick={() => setActiveCategory(cat)}
                className={`px-6 py-2 rounded-full font-medium transition-all flex items-center gap-2 text-sm ${
                  isActive
                    ? cat === 'all'
                      ? 'bg-blue-50 dark:bg-blue-900/30 text-secondary border border-blue-100 dark:border-blue-800'
                      : categoryAccents[cat as TemplateCategory].active
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 border border-transparent'
                }`}
              >
                {cat !== 'all' && (
                  <span className="material-icons-outlined text-lg">
                    {categoryIcons[cat as TemplateCategory]}
                  </span>
                )}
                {getCategoryLabel(cat)}
              </button>
            );
          })}
        </div>
      </div>

      {/* Template Grid */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="material-icons-outlined animate-spin text-secondary text-4xl">autorenew</span>
        </div>
      ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" role="radiogroup" aria-label={t(locale, 'selectTemplate')}>
        {filteredTemplates.map((template) => {
          const isSelected = selectedTemplateId === template.id;
          const isUnavailable = !template.is_available;
          const accent = getTemplateAccent(template.slug);

          return (
            <button
              key={template.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={isUnavailable}
              onClick={() => !isUnavailable && onSelect(template)}
              className={`relative p-6 rounded-[16px] border-2 text-start transition-all focus:outline-none focus:ring-2 ${accent.ring} ${
                isUnavailable
                  ? 'opacity-70 cursor-not-allowed border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800'
                  : isSelected
                  ? `${accent.border} ${accent.bg} cursor-pointer`
                  : `border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 ${accent.hover} cursor-pointer`
              }`}
            >
              {isUnavailable && (
                <div className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'}`}>
                  <span className="bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-[10px] px-2 py-1 rounded-md font-bold">
                    {t(locale, 'comingSoon')}
                  </span>
                </div>
              )}
              <div className={`flex justify-between items-start mb-4 ${isRTL ? 'flex-row-reverse' : ''}`}>
                <span className={`material-icons-outlined text-3xl ${accent.icon}`}>
                  {template.icon}
                </span>
                <h3 className="text-xl font-bold text-gray-900">
                  {getTemplateName(template)}
                </h3>
              </div>
              <p className={`text-gray-600 dark:text-gray-400 text-sm leading-relaxed ${isRTL ? 'text-right' : 'text-left'}`}>
                {getTemplateDescription(template)}
              </p>
            </button>
          );
        })}
      </div>
      )}
    </div>
  );
}
